import React from 'react';
import { useDispatch } from 'react-redux';
import { Flex } from '@adobe/react-spectrum';
import './PowerLevels.css';
import { Helmet } from 'react-helmet';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { setAppRoute } from '../../data/redux/slices/appSlice';

// Single source of truth for the tier ladder rendered on the page.
// `tone` maps to a CSS modifier for the accent strip + range chip.
const POWER_LEVEL_TIERS = [
    {
        range: '1 – 4.99x',
        tone: 'casual',
        title: 'Casual',
        description: 'Precons and low-power brews fall into this range.',
    },
    {
        range: '5 – 6.49x',
        tone: 'upgraded',
        title: 'Upgraded casual',
        description:
            'Upgraded precons and stronger brews show up here. Decks start to become more focused on a specific game plan.',
    },
    {
        range: '6.5 – 7.59x',
        tone: 'mid',
        title: 'Mid power',
        description:
            'More optimized with a focused gameplan, but held back by things like manabase quality. Often intentional — builders pulling punches to keep the deck from running away with the table.',
    },
    {
        range: '7.6 – 8.99x',
        tone: 'high',
        title: 'High power',
        description:
            'These decks have a very focused path to winning, mostly optimized in manabase efficiency and speed. What usually keeps them out of fringe cEDH is budget-conscious deck building.',
    },
    {
        range: '9 – 9.29x',
        tone: 'fringe',
        title: 'Fringe cEDH and cEDH',
        description:
            'A bit of a mix. Fringe cEDH decks running unproven commanders, meta commanders that don’t typically place well, and high-tier meta commanders built less efficiently than they could be.',
    },
    {
        range: '9.3 – 10.29x',
        tone: 'cedh3',
        title: 'cEDH – Tier 3',
        description: 'Decks running meta commanders that do not consistently place well in tournament results.',
    },
    {
        range: '10.3 – 10.69x',
        tone: 'cedh2',
        title: 'cEDH – Tier 2',
        description: 'The commanders of these decks typically place in the low end of top 16 results.',
    },
    {
        range: '10.7 – 10.99x',
        tone: 'cedh1',
        title: 'cEDH – Tier 1',
        description:
            'Best of the best. The sweatiest of the sweatiest. These commanders dominate the top 16 results of most cEDH tournaments.',
    },
];

export function PowerLevels() {
    const dispatch = useDispatch();

    const helmetName = 'Power Levels';
    const helmetCanonical = 'https://www.commandersalt.com/powerlevels';
    const helmetDescription = 'Power Levels';
    const helmetTitle = 'Commandersalt.com - Power Levels';

    const handleMetaClick = () => {
        logEvent(getAnalytics(), 'powerlevels_meta_click');
        dispatch(setAppRoute({ route: '/meta' }));
    };

    const handleAlgorithmClick = () => {
        logEvent(getAnalytics(), 'powerlevels_algorithm_click');
        dispatch(setAppRoute({ route: '/algorithm' }));
    };

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />

            <div className="powerLevelsRoot">
                <section className="powerLevelsHero panelGradient">
                    <div className="powerLevelsHeroMain">
                        <span className="powerLevelsHeroEyebrow">Power Levels</span>
                        <h1 className="powerLevelsHeroTitle">How we rank decks 1 – 10</h1>
                        <p className="powerLevelsHeroSubtitle">
                            Power-level scoring isn’t an exact science — there’s no universally accepted scale. With
                            that in mind, the logic the site uses to rank decks is based on the criteria below. Each
                            band is a window of absolute score values; decks inside a band share broadly similar shape,
                            speed, and optimization.
                        </p>
                    </div>
                    <div className="powerLevelsHeroLinks">
                        <button type="button" className="powerLevelsHeroLink" onClick={handleMetaClick}>
                            <span className="powerLevelsHeroLinkArrow" aria-hidden="true">
                                →
                            </span>
                            cEDH tiers and meta defined here
                        </button>
                        <button type="button" className="powerLevelsHeroLink" onClick={handleAlgorithmClick}>
                            <span className="powerLevelsHeroLinkArrow" aria-hidden="true">
                                →
                            </span>
                            Read about the scoring algorithm here
                        </button>
                    </div>
                </section>

                <section className="powerLevelsLadder" aria-label="Power level tier ladder">
                    {POWER_LEVEL_TIERS.map((tier) => (
                        <article key={tier.tone} className={`powerLevelsTier powerLevelsTier--${tier.tone}`}>
                            <div className="powerLevelsTierRange">
                                <span className="powerLevelsTierRangeValue">{tier.range}</span>
                            </div>
                            <div className="powerLevelsTierBody">
                                <h2 className="powerLevelsTierTitle">{tier.title}</h2>
                                <p className="powerLevelsTierDescription">{tier.description}</p>
                            </div>
                        </article>
                    ))}
                </section>
            </div>
        </Flex>
    );
}
